import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik } from "formik";

// Material Imports
import {
  Chip,
  Select,
  MenuItem,
  Button,
  TextField,
  FormLabel,
  FormControl,
  OutlinedInput,
  FormHelperText,
  InputLabel,
} from "@mui/material";
import TodayOutlined from "@mui/icons-material/TodayOutlined";
import Add from "@mui/icons-material/Add";
import { makeStyles } from "@mui/styles";

import { TASK_STATUS_OPTIONS, TASK_STATUS_COLOR_MAP } from "static/constants";
import usePatientTasks from "../usePatientTasks";
import ModalWrapper from "components/common/modals/modalWrapper/ModalWrapper";
import { useSelector } from "react-redux";
import { filterNonUCHAccounts } from "utils/account.util";
import { getFormattedDate } from "utils/date";
import { DATE_FORMAT } from "static/date";
import { isValidArray } from "utils/array";

// Validation Schema
const validationSchema = Yup.object({
  description: Yup.string()
    .required("Task Description is required")
    .test(
      "no-whitespace-only",
      "Task Description cannot contain only spaces",
      (value) => !value || value.trim().length > 0
    ),
  status: Yup.string().required("Status is required"),
  account: Yup.string().required("Account is required"),
  notes: Yup.array().of(
    Yup.object().shape({
      note: Yup.string().test(
        "no-whitespace-only",
        "Communication Notes cannot contain only spaces",
        (value) => !value || value.trim().length > 0
      ),
    })
  ),
});

const useStyles = makeStyles(() => ({
  label: {
    fontFamily: "Roboto",
    fontWeight: 400,
    fontStyle: "normal",
    fontSize: "12px",
    marginBottom: "8px",
  },
  dateInfoBanner: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: "#EDF7ED",
    borderRadius: "4.66px",
    padding: "8px",
    fontFamily: "Roboto",
    fontWeight: 400,
    fontSize: "12px",
    marginTop: "10px",
    marginBottom: "5px",
  },
  todayIcon: {
    color: "#757575",
    width: "12px",
    height: "13px",
  },
  statusGroup: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
  statusChip: {
    flex: "1 1 calc(25% - 6px)",
    justifyContent: "center",
    minWidth: 0,
    borderRadius: "50px",
    "& .MuiChip-label": {
      fontSize: "10px !important",
    },
  },
  patientCard: {
    border: "1px solid #BFD685",
    borderRadius: "4.66px",
    backgroundColor: "#F7FBEE",
    padding: "8px 16px",
  },
  patientName: {
    color: "#212121",
    fontSize: "14px",
    fontWeight: 500,
    fontFamily: "Roboto",
    marginBottom: "5px",
  },
  dobSeparator: {
    margin: "0 4px",
  },
  patientCardDisabled: {
    backgroundColor: "#ECECEC",
    borderColor: "#E0E0E0",
  },
  notesField: {
    "& .MuiOutlinedInput-root fieldset": {
      borderColor: "#e0e0e0",
    },
  },
  notesHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  noteCard: {
    backgroundColor: "#F5F7FB",
    border: "1px solid #E0E0E0",
    borderRadius: "4px",
    padding: "10px 12px",
    marginBottom: "8px",
  },
  noteText: {
    color: "#212121",
    fontSize: "14px",
    fontFamily: "Roboto",
    fontWeight: 400,
    marginBottom: "4px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  noteTimestamp: {
    fontFamily: "Roboto",
    fontSize: "11px",
    color: "#757575",
  },
  disabledInput: {
    "& .Mui-disabled": {
      backgroundColor: "#ECECEC",
      borderColor: "#E0E0E0",
    },
  },
}));

const TaskModal = () => {
  const classes = useStyles();
  const { accounts } = useSelector((state) => state.account);
  const { profile } = useSelector((state) => state.transmission);
  const filteredAccounts = filterNonUCHAccounts(accounts);
  const {
    theme,
    onClose,
    isSubmitting,
    patientTask,
    isPatientTaskModal,
    patientTaskAction,
    originalPatientTask,
    handleSubmit,
    handleInputChange,
    currentUser,
  } = usePatientTasks();

  const formikRef = React.useRef(null);

  const [notesList, setNotesList] = useState([]);
  const [newNoteText, setNewNoteText] = useState("");

  useEffect(() => {
    if (isPatientTaskModal && patientTaskAction === "Edit") {
      const existingNotes = originalPatientTask?.notes;
      const existingNote = originalPatientTask?.note;
      if (isValidArray(existingNotes)) {
        setNotesList(
          existingNotes.map((n) => ({
            note: n.note ?? n,
            addedAt: n.addedAt ? new Date(n.addedAt) : new Date(),
            addedBy: n.addedBy ?? currentUser?.name,
          }))
        );
      } else if (existingNote?.note) {
        setNotesList([
          {
            note: existingNote.note,
            addedAt: existingNote.addedAt
              ? new Date(existingNote.addedAt)
              : new Date(),
            addedBy: existingNote.addedBy ?? currentUser?.name,
          },
        ]);
      } else {
        setNotesList([]);
      }
    } else {
      setNotesList([]);
    }
    setNewNoteText("");
  }, [isPatientTaskModal]);

  const handleAddNote = () => {
    if (newNoteText.trim()) {
      const newList = [
        ...notesList,
        { note: newNoteText.trim(), addedAt: new Date() },
      ];
      setNotesList(newList);
      formikRef.current?.setFieldValue("notes", newList);
      setNewNoteText("");
    }
  };

  const actionButtons = [
    {
      label: "Cancel",
      variant: "outlined",
      onClick: onClose,
      disabled: isSubmitting,
    },
    {
      label: patientTaskAction === "Edit" ? "Save Changes" : "Add Task",
      variant: "contained",
      onClick: () => formikRef.current?.submitForm(),
      disabled: isSubmitting,
      isLoading: isSubmitting,
    },
  ];

  return (
    <div>
      <ModalWrapper
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        show={isPatientTaskModal}
        onClose={onClose}
        title={
          patientTaskAction === "Edit" ? "Update Task Information" : "Add Task"
        }
        maxWidth="sm"
        showContentDivider
        actionButtons={actionButtons}
      >
        <Formik
          innerRef={formikRef}
          initialValues={{ ...patientTask, notes: [] }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            const finalNotes = newNoteText.trim()
              ? [
                  ...notesList,
                  { note: newNoteText.trim(), addedAt: new Date() },
                ]
              : notesList;
            handleSubmit({ ...values, notes: finalNotes });
          }}
          enableReinitialize
          validateOnChange={false}
          validateOnBlur={false}
        >
          {({ setFieldValue, errors, touched, submitCount }) => {
            const submitted = submitCount > 0;
            return (
              <div
                className={`d-flex flex-column g-1 ${classes.disabledInput}`}
              >
                <div className={classes.dateInfoBanner}>
                  <TodayOutlined className={classes.todayIcon} />
                  {patientTaskAction === "Edit"
                    ? `Assigned on ${getFormattedDate(originalPatientTask?.created_at, "MM/DD/YY")}`
                    : `Date will be recorded as today (${getFormattedDate(new Date(), "MM/DD/YY")})`}
                </div>

                <FormControl
                  fullWidth
                  variant="outlined"
                  style={{
                    backgroundColor: theme.palette.background.paper,
                    ...(patientTaskAction === "Edit" && {
                      marginTop: "15px",
                    }),
                  }}
                  error={Boolean(errors.account && submitted)}
                  required
                >
                  <InputLabel htmlFor="accounts" id="label-accounts">
                    Account
                  </InputLabel>
                  <Select
                    id="accounts"
                    labelId="label-accounts"
                    disabled
                    name="account"
                    input={<OutlinedInput label="Account" />}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleInputChange(value, "account");
                      setFieldValue("account", value);
                    }}
                    value={patientTask.account}
                    MenuProps={{ PaperProps: { style: { maxHeight: 240 } } }}
                  >
                    {filteredAccounts &&
                      filteredAccounts?.map(({ _id, name }) => (
                        <MenuItem key={_id} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                  </Select>
                  {submitted && errors.account && (
                    <FormHelperText>{errors.account}</FormHelperText>
                  )}
                </FormControl>

                {profile?.name && (
                  <FormControl>
                    <div
                      className={`${classes.patientCard} ${classes.patientCardDisabled} d-flex align-items-center justify-between`}
                    >
                      <div>
                        <p className={classes.patientName}>{profile?.name}</p>
                        <p className="mt-1 mb-1">
                          {profile?.dob && (
                            <span className={classes.label}>
                              DOB:
                              {getFormattedDate(
                                profile?.dob,
                                DATE_FORMAT.DEFAULT
                              )}
                            </span>
                          )}
                          {profile?.dob &&
                            profile?.evoke_id &&
                            profile?.account && (
                              <span className={classes.dobSeparator}>|</span>
                            )}
                          {profile?.evoke_id && (
                            <span className={classes.label}>
                              Evoke ID: {profile?.evoke_id}
                            </span>
                          )}
                          {(profile?.dob || profile?.evoke_id) &&
                            profile?.account && (
                              <span className={classes.dobSeparator}>|</span>
                            )}
                          {profile?.account && (
                            <span className={classes.label}>
                              Account: {profile?.account}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </FormControl>
                )}

                <FormControl
                  fullWidth
                  error={Boolean(errors.status && submitted)}
                  className="task-modal__input"
                >
                  <FormLabel id="status-label" className={classes.label}>
                    Status
                  </FormLabel>
                  <div
                    role="group"
                    aria-labelledby="status-label"
                    className={classes.statusGroup}
                  >
                    {TASK_STATUS_OPTIONS.map((status) => {
                      const color = TASK_STATUS_COLOR_MAP[status.value];
                      const isSelected = patientTask.status === status.value;

                      return (
                        <Chip
                          key={status.value}
                          variant="outlined"
                          size="small"
                          clickable
                          label={status.label}
                          aria-pressed={isSelected}
                          onClick={() => {
                            handleInputChange(status.value, "status");
                            setFieldValue("status", status.value);
                          }}
                          className={classes.statusChip}
                          icon={
                            <span
                              className={classes.statusDot}
                              style={{ backgroundColor: color }}
                            />
                          }
                          style={{
                            borderColor: isSelected ? color : "#E0E0E0",
                            fontWeight: isSelected ? 600 : 400,
                          }}
                          sx={{
                            "& .MuiChip-label": {
                              color: isSelected ? color : "#444444",
                            },
                          }}
                        />
                      );
                    })}
                  </div>
                  {submitted && errors.status && (
                    <FormHelperText error>{errors.status}</FormHelperText>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel id="description-label" className={classes.label}>
                    Task Description
                  </FormLabel>
                  <TextField
                    inputProps={{
                      style: { alignItems: "top" },
                    }}
                    multiline
                    fullWidth
                    minRows={6}
                    maxRows={13}
                    className="task-modal__textfield mb-0"
                    variant="outlined"
                    id="description"
                    placeholder="What needs to happen..."
                    name="description"
                    value={patientTask.description}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleInputChange(value, "description");
                      setFieldValue("description", e.target.value);
                    }}
                    error={Boolean(errors.description && submitted)}
                    helperText={submitted && errors.description}
                  />
                </FormControl>

                <FormControl>
                  <div className={classes.notesHeader}>
                    <FormLabel
                      className={classes.label}
                      style={{ marginBottom: 0 }}
                    >
                      Communication Notes (
                      {notesList.length > 0
                        ? `${notesList.length} added`
                        : "optional"}
                      )
                    </FormLabel>
                    <Button
                      size="small"
                      startIcon={<Add />}
                      onClick={handleAddNote}
                      disabled={isSubmitting || !newNoteText.trim()}
                      style={{ textTransform: "none", fontSize: "12px" }}
                      variant="outlined"
                      color="secondary"
                      sx={{
                        "&.Mui-disabled": {
                          border: "1px solid #969696",
                          borderRadius: "4px",
                          background: "#fff !important",
                          color: "#969696 !important",
                          height: "28px",
                          "& .MuiButton-startIcon": {
                            color: "#969696",
                          },
                        },
                      }}
                    >
                      Add Notes
                    </Button>
                  </div>
                  <TextField
                    className={`view-pdf__textarea task-modal__input ${classes.notesField}`}
                    placeholder="Calls, messages or handoff details for the care team.."
                    id="notes"
                    data-text="communication-note-field"
                    multiline
                    minRows={3}
                    maxRows={8}
                    fullWidth
                    variant="outlined"
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                  />
                  {notesList.length > 0 && (
                    <div className="mt-2">
                      {[...notesList]
                        .sort(
                          (a, b) => new Date(b.addedAt) - new Date(a.addedAt)
                        )
                        .map((n, index) => (
                          <div key={index} className={classes.noteCard}>
                            <p className={classes.noteText}>{n.note}</p>
                            <span className={classes.noteTimestamp}>
                              {getFormattedDate(
                                n.addedAt,
                                DATE_FORMAT.SHORT_DATE_TIME
                              )}
                            </span>
                          </div>
                        ))}
                    </div>
                  )}
                </FormControl>
              </div>
            );
          }}
        </Formik>
      </ModalWrapper>
    </div>
  );
};

export default TaskModal;
